import { useCallback, useEffect, useRef, useState } from "react";
import {
  indexedDBService,
  type PanoramaCache,
  type ThumbnailCache,
} from "@/services/indexedDBService";

interface CacheStats {
  totalSize: number;
  panoramaCount: number;
  thumbnailCount: number;
  lastCleanup: number;
}

interface UseIndexedDBCacheReturn {
  // State
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  cacheStats: CacheStats | null;

  // Panorama methods
  cachePanorama: (
    panoramaId: string,
    projectId: string,
    craftspaceId: string,
    data: ArrayBuffer,
    mimeType: string,
    name: string,
  ) => Promise<void>;
  getPanorama: (panoramaId: string) => Promise<PanoramaCache | null>;
  getPanoramasByProject: (projectId: string) => Promise<PanoramaCache[]>;

  // Thumbnail methods
  cacheThumbnail: (
    panoramaId: string,
    projectId: string,
    craftspaceId: string,
    data: ArrayBuffer,
    mimeType: string,
    isWebP?: boolean,
  ) => Promise<void>;
  getThumbnail: (panoramaId: string, isWebP?: boolean) => Promise<ThumbnailCache | null>;
  getThumbnailsByProject: (projectId: string) => Promise<ThumbnailCache[]>;
  cacheThumbnailFromUrl: (
    panoramaId: string,
    projectId: string,
    craftspaceId: string,
    url: string,
    isWebP?: boolean,
  ) => Promise<void>;

  // Cache management
  getCacheStats: () => Promise<void>;
  clearPanoramaCache: (panoramaId: string) => Promise<void>;
  clearProjectCache: (projectId: string) => Promise<void>;
  clearAllCache: () => Promise<void>;

  // Utility methods
  createBlobURL: (data: ArrayBuffer, mimeType: string) => string;
  revokeBlobURL: (url: string) => void;
}

export const useIndexedDBCache = (): UseIndexedDBCacheReturn => {
  const [isInitialized, setIsInitialized] = useState(() => indexedDBService.isReady);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cacheStats, setCacheStats] = useState<CacheStats | null>(null);

  const initRef = useRef(false);
  // Stable ref so callbacks below don't change identity when isInitialized flips
  const isInitializedRef = useRef(isInitialized);
  isInitializedRef.current = isInitialized;

  // Cache management methods - define these first
  const getCacheStats = useCallback(async (): Promise<void> => {
    try {
      if (!isInitialized) return;

      const stats = await indexedDBService.getCacheStats();
      if (stats) {
        setCacheStats(stats);
      }
    } catch (err) {
      console.error("Failed to get cache stats:", err);
    }
  }, [isInitialized]);

  // Initialize IndexedDB on mount
  useEffect(() => {
    const initDB = async () => {
      if (initRef.current) return;

      try {
        setIsLoading(true);
        setError(null);

        await indexedDBService.init();
        setIsInitialized(true);

        // Get initial cache stats
        await getCacheStats();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to initialize IndexedDB";
        setError(errorMessage);
        console.error("❌ IndexedDB cache hook initialization failed:", err);

        // Set initialized to false but don't block the app
        setIsInitialized(false);
        console.warn("⚠️ Continuing without IndexedDB caching - thumbnails will use network URLs");
      } finally {
        setIsLoading(false);
        initRef.current = true;
      }
    };

    initDB();
  }, [getCacheStats]); // Add getCacheStats dependency since it's called in initDB

  // Panorama caching methods
  const cachePanorama = useCallback(
    async (
      panoramaId: string,
      projectId: string,
      craftspaceId: string,
      data: ArrayBuffer,
      mimeType: string,
      name: string,
    ): Promise<void> => {
      try {
        if (!isInitialized) {
          console.warn("⚠️ IndexedDB not available - skipping panorama cache");
          return;
        }

        await indexedDBService.cachePanorama(
          panoramaId,
          projectId,
          craftspaceId,
          data,
          mimeType,
          name,
        );

        // Update cache stats
        await getCacheStats();
      } catch (err) {
        console.warn("⚠️ Failed to cache panorama (continuing):", err);
        // Don't throw - just log and continue
      }
    },
    [isInitialized, getCacheStats],
  );

  const getPanorama = useCallback(
    async (panoramaId: string): Promise<PanoramaCache | null> => {
      try {
        if (!isInitializedRef.current) return null;
        return await indexedDBService.getPanorama(panoramaId);
      } catch (err) {
        console.error("Failed to get panorama from cache:", err);
        return null;
      }
    },
    [], // stable — reads isInitialized via ref
  );

  const getPanoramasByProject = useCallback(
    async (projectId: string): Promise<PanoramaCache[]> => {
      try {
        if (!isInitializedRef.current) return [];
        return await indexedDBService.getPanoramasByProject(projectId);
      } catch (err) {
        console.error("Failed to get panoramas by project from cache:", err);
        return [];
      }
    },
    [], // stable
  );

  // Thumbnail caching methods
  const cacheThumbnail = useCallback(
    async (
      panoramaId: string,
      projectId: string,
      craftspaceId: string,
      data: ArrayBuffer,
      mimeType: string,
      isWebP: boolean = false,
    ): Promise<void> => {
      try {
        if (!isInitialized) {
          console.warn("⚠️ IndexedDB not available - skipping thumbnail cache");
          return;
        }

        await indexedDBService.cacheThumbnail(
          panoramaId,
          projectId,
          craftspaceId,
          data,
          mimeType,
          isWebP,
        );

        // Update cache stats
        await getCacheStats();
      } catch (err) {
        console.warn("⚠️ Failed to cache thumbnail (continuing):", err);
        // Don't throw - just log and continue
      }
    },
    [isInitialized, getCacheStats],
  );

  const getThumbnail = useCallback(
    async (panoramaId: string, isWebP: boolean = false): Promise<ThumbnailCache | null> => {
      try {
        if (!isInitializedRef.current) return null;
        return await indexedDBService.getThumbnail(panoramaId, isWebP);
      } catch (err) {
        console.error("Failed to get thumbnail from cache:", err);
        return null;
      }
    },
    [], // stable — reads isInitialized via ref
  );

  const getThumbnailsByProject = useCallback(
    async (projectId: string): Promise<ThumbnailCache[]> => {
      try {
        if (!isInitializedRef.current) return [];
        return await indexedDBService.getThumbnailsByProject(projectId);
      } catch (err) {
        console.error("Failed to get thumbnails by project from cache:", err);
        return [];
      }
    },
    [], // stable
  );

  const cacheThumbnailFromUrl = useCallback(
    async (
      panoramaId: string,
      projectId: string,
      craftspaceId: string,
      url: string,
      isWebP: boolean = false,
    ): Promise<void> => {
      try {
        if (!isInitialized) {
          console.warn("⚠️ IndexedDB not available - skipping thumbnail re-cache");
          return;
        }

        await indexedDBService.cacheThumbnailFromUrl(
          panoramaId,
          projectId,
          craftspaceId,
          url,
          isWebP,
        );

        // Update cache stats
        await getCacheStats();
      } catch (err) {
        console.warn("⚠️ Failed to re-cache thumbnail (continuing):", err);
        // Don't throw - just log and continue
      }
    },
    [isInitialized, getCacheStats],
  );

  // Cache management methods
  const clearPanoramaCache = useCallback(
    async (panoramaId: string): Promise<void> => {
      try {
        if (!isInitialized) return;

        await indexedDBService.clearPanoramaCache(panoramaId);

        // Update cache stats
        await getCacheStats();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to clear panorama cache";
        setError(errorMessage);
        throw err;
      }
    },
    [isInitialized, getCacheStats],
  );

  const clearProjectCache = useCallback(
    async (projectId: string): Promise<void> => {
      try {
        if (!isInitialized) return;

        await indexedDBService.clearProjectCache(projectId);

        // Update cache stats
        await getCacheStats();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to clear project cache";
        setError(errorMessage);
        throw err;
      }
    },
    [isInitialized, getCacheStats],
  );

  const clearAllCache = useCallback(async (): Promise<void> => {
    try {
      if (!isInitialized) return;

      await indexedDBService.clearAllCache();

      // Update cache stats
      await getCacheStats();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to clear all cache";
      setError(errorMessage);
      throw err;
    }
  }, [isInitialized, getCacheStats]);

  // Utility methods
  const createBlobURL = useCallback((data: ArrayBuffer, mimeType: string): string => {
    return indexedDBService.createBlobURL(data, mimeType);
  }, []);

  const revokeBlobURL = useCallback((url: string): void => {
    indexedDBService.revokeBlobURL(url);
  }, []);

  // Clear error when component unmounts or when operations succeed
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return {
    // State
    isInitialized,
    isLoading,
    error,
    cacheStats,

    // Panorama methods
    cachePanorama,
    getPanorama,
    getPanoramasByProject,

    // Thumbnail methods
    cacheThumbnail,
    getThumbnail,
    getThumbnailsByProject,
    cacheThumbnailFromUrl,

    // Cache management
    getCacheStats,
    clearPanoramaCache,
    clearProjectCache,
    clearAllCache,

    // Utility methods
    createBlobURL,
    revokeBlobURL,
  };
};
